import { useEffect, useRef, useState, useMemo } from "preact/hooks";
import { useWebSocket } from "../../../shared/contexts";
import { GameState, GameStateEnum } from "../../../shared/types/global.types";
import { MT, PHASE } from "../../../shared/types/proto/mt";
import { clamp, lerp } from "../../../shared/utils/interpolation";
import { BetEvent, useBetFeedStore } from "../../../store/betFeedStore";
import { useProvablyFairStore } from "../../../store/provablyFairStore";
import { useUserStore } from "../../../store/userStore";

export type GameStatus = {
  multiplier: number;
  gameState: GameState;
  countdown: number;
  countdownTotal: number;
  countdownKey: number;
  history: number[];
  missedWindows: number;
};
const TICK_SIZE_TIME = 100;
const MAX_HISTORY = 30;

function calculateNewX(
  fixated_x: number,
  prev_x: number,
  t_current: number,
  t_start: number
): number {
  const growth_speed = fixated_x - prev_x;
  const potential_x = fixated_x + growth_speed;

  const t_end = t_start + TICK_SIZE_TIME;

  const t_normalized =
    (clamp(t_current, t_start, t_end) - t_start) / (t_end - t_start);

  const new_x = lerp(fixated_x, potential_x, t_normalized);

  return new_x;
}

export const useGameStatus = (): GameStatus => {
  const { onMessage, missedWindows, sendProto, status } = useWebSocket();

  const {
    addNewBets,
    applyCancels,
    applyCashouts,
    resetOnPreRound,
    setLastRoundX,
    setPlayersBettingCounters,
  } = useBetFeedStore();
  const { setNextClientSeed, setNextServerHash } = useProvablyFairStore();

  const [multiplier, setMultiplier] = useState(1);
  const [gameState, setGameState] = useState<GameState>(
    GameStateEnum.COUNTDOWN
  );
  const [countdown, setCountdown] = useState(6);
  const [countdownTotal, setCountdownTotal] = useState(6);
  const [deadlineVersion, setDeadlineVersion] = useState(0);
  const [history, setHistory] = useState<number[]>([]);
  const countdownDeadlineRef = useRef<number | null>(null);
  const prevXRef = useRef<number>(1);

  const { setProfile, updateProfile } = useUserStore();

  const fixatedXRef = useRef<number>(1);
  const tStartRef = useRef<number | null>(null);

  const scheduleCountdown = (
    durationMs?: number | null,
    baseTimestamp?: number | null
  ) => {
    const d = Number(durationMs ?? 0);
    const base = Number(baseTimestamp ?? Date.now());
    if (Number.isFinite(d) && Number.isFinite(base)) {
      const elapsed = Date.now() - base;
      const remainingMs = Math.max(0, d - elapsed);
      setCountdownTotal(Math.ceil(d / 1000));
      setCountdown(remainingMs / 1000);
      countdownDeadlineRef.current = Date.now() + remainingMs;
      setDeadlineVersion((v) => v + 1);
    }
  };

  const updateCountdown = () => {
    const deadline = countdownDeadlineRef.current;
    if (!deadline) return;
    const remainingMs = Math.max(0, deadline - Date.now());
    setCountdown(remainingMs / 1000);
  };

  useEffect(() => {
    const offGameState = onMessage(MT.GAME_STATE, (message) => {
      const snapshot = message.gameState;
      if (!snapshot) return;
      const phase = snapshot.currentPhase;
      switch (phase) {
        case PHASE.PRE_ROUND:
          setGameState(GameStateEnum.COUNTDOWN);
          scheduleCountdown(
            Number(snapshot.phaseDurationMs),
            Number(snapshot.phaseStartTime ?? message.timestamp ?? Date.now())
          );
          break;
        case PHASE.IN_PROGRESS:
          setGameState(GameStateEnum.CLIMBING);
          break;
        case PHASE.ENDING:
          setGameState(GameStateEnum.CRASHED);
          break;
        case PHASE.STARTING:
        default:
          break;
      }

      const current = snapshot.currentMultiplier;
      if (current != null) {
        prevXRef.current = multiplier;
        fixatedXRef.current = current;
        tStartRef.current = Date.now();
        setMultiplier(current);
      }

      const snapHist = Array.isArray(snapshot.history)
        ? [...snapshot.history]
        : [];
      setPlayersBettingCounters({
        activeBetsCount: Number(snapshot.activeBets?.length ?? 0),
        cashedOutCount: 0,
        totalCount: Number(snapshot.activeBets?.length ?? 0),
        totalCashedOutAmount: 0,
      } as any);
      addNewBets((snapshot.activeBets as BetEvent[]) ?? []);
      if (snapHist.length) {
        const capped = snapHist.slice(0, MAX_HISTORY);
        setHistory(capped);
        setLastRoundX(capped[0] as number);
      }
    });

    return () => offGameState();
  }, [onMessage]);

  useEffect(() => {
    const offMultiplier = onMessage(MT.MULTIPLIER_UPDATE, (message) => {
      const update = message.multiplierUpdate;
      const nextMultiplier = update?.multiplier;
      if (nextMultiplier != null) {
        prevXRef.current = multiplier;
        fixatedXRef.current = nextMultiplier;
        tStartRef.current = Date.now();

        const currentTime = Date.now();
        const interpolatedX = calculateNewX(
          nextMultiplier,
          multiplier,
          currentTime,
          tStartRef.current
        );
        setMultiplier(interpolatedX);
      }
    });

    const offPreRound = onMessage(MT.PRE_ROUND_START, (message) => {
      setGameState(GameStateEnum.COUNTDOWN);
      setMultiplier(1);
      resetOnPreRound();
      const preRound = message.preRoundStart;
      scheduleCountdown(
        preRound?.durationMs,
        Number(preRound?.startTime ?? message.timestamp ?? Date.now())
      );
      if (preRound?.publicHash) {
        setNextServerHash(preRound.publicHash);
      }
    });

    const offRoundStart = onMessage(MT.ROUND_START, () => {
      setGameState(GameStateEnum.CLIMBING);
    });

    const offRoundCrash = onMessage(MT.ROUND_CRASH, (message) => {
      setGameState(GameStateEnum.CRASHED);
      const crashPoint = message.roundCrash?.crashPoint;
      if (crashPoint) {
        setLastRoundX(crashPoint);
        setHistory((prev) => [crashPoint, ...prev].slice(0, MAX_HISTORY));
      }
    });
    const offActiveBetsCount = onMessage(
      MT.TOTAL_BETS_COUNT_UPDATE,
      (message) => {
        const update = message.totalBetsCount;
        if (update) {
          setPlayersBettingCounters(update);
        }
      }
    );
    const offRoomBets = onMessage(MT.ROOM_BETS_NOTIFICATION, (message) => {
      const notif = message.roomBetsNotification;
      if (!notif) return;
      const newList = Array.isArray(notif.newBets) ? notif.newBets : [];
      if (newList.length) {
        const preparedNew = newList.filter(Boolean).map(
          (n) =>
            ({
              roundId: Number(n?.roundId ?? 0),
              userId: Number(n?.userId ?? 0),
              slotId: Number(n?.slotId ?? 0),
              amount: Number(n?.amount ?? 0),
              nickname: String(n?.nickname ?? ""),
              avatar: String(n?.avatar ?? "Avatar_1"),
              multiplier: 0,
              payout: 0,
              id: `${Number(n?.roundId ?? 0)}:${Number(n?.userId ?? 0)}:${Number(n?.slotId ?? 0)}`,
            }) as BetEvent
        );
        if (preparedNew.length) addNewBets(preparedNew);
      }

      const cancelList = Array.isArray(notif.cancelledBets)
        ? notif.cancelledBets
        : [];
      if (cancelList.length) {
        const preparedCancels = cancelList.filter(Boolean).map((n) => ({
          roundId: n?.roundId != null ? Number(n.roundId) : undefined,
          userId: Number(n?.userId ?? 0),
          slotId: n?.slotId != null ? Number(n.slotId) : undefined,
        }));
        if (preparedCancels.length) applyCancels(preparedCancels);
      }

      const cashoutList = notif.cashouts ?? [];
      if (cashoutList.length) {
        const preparedCashouts = cashoutList.filter(Boolean).map((n) => ({
          roundId: n?.roundId != null ? Number(n.roundId) : undefined,
          userId: Number(n?.userId ?? 0),
          slotId: n?.slotId != null ? Number(n.slotId) : undefined,
          multiplier: Number(n?.multiplier ?? 0),
          payout: Number(n?.payout ?? 0),
        }));
        if (preparedCashouts.length) applyCashouts(preparedCashouts);
      }
    });

    const offBigWin = onMessage(MT.BIG_WIN_NOTIFICATION, (message) => {
      const notif = message.bigWinNotification;

      if (!notif) return;
      const bigWinCashout = {
        roundId: Number(notif.roundId ?? 0),
        userId: 0,
        slotId: notif.slotId!,
        multiplier: Number(notif.multiplier ?? 0),
        payout: Number(notif.amount ?? 0),
        isBigWin: true,
        playerName: notif.playerName ?? "",
        avatar: notif.avatar ?? "",
      };
      applyCashouts([bigWinCashout]);
    });
    return () => {
      offRoomBets();
      offMultiplier();
      offPreRound();
      offRoundStart();
      offRoundCrash();
      offActiveBetsCount();
      offBigWin();
    };
  }, [onMessage]);

  useEffect(() => {
    const offClientSeed = onMessage(MT.YOUR_CLIENT_SEED, (message) => {
      const cs = message.yourClientSeed?.clientSeed;
      if (cs) setNextClientSeed(cs);
    });
    return () => {
      offClientSeed();
    };
  }, [onMessage]);

  useEffect(() => {
    const offWallet = onMessage(MT.WALLET_UPDATE, (message) => {
      const update = message.walletUpdate;
      if (update) {
        updateProfile({
          balance: update.balance,
        });
      }
    });

    // Ответ на `GET_WALLET`, который отправляется после исходного снимка
    // состояния комнаты: начальный баланс сам не приходит (§10 demo-lobby).
    const offWalletResponse = onMessage(MT.WALLET_RESPONSE, (message) => {
      const wallet = message.walletResponse;
      if (wallet) {
        updateProfile({
          balance: wallet.balance,
          reserved: wallet.reserved,
        });
      }
    });

    return () => {
      offWallet();
      offWalletResponse();
    };
  }, [onMessage, updateProfile]);

  useEffect(() => {
    if (gameState !== GameStateEnum.COUNTDOWN || !countdownDeadlineRef.current)
      return;
    updateCountdown();
    const id = setInterval(updateCountdown, 250);
    return () => clearInterval(id);
  }, [gameState, deadlineVersion]);

  useEffect(() => {
    if (status === "open") {
      const msg = {
        type: MT.GET_USER_PROFILE,
        getUserProfile: {},
      } as any;
      sendProto(msg);
    }
  }, [status, sendProto]);

  useEffect(() => {
    const off = onMessage(MT.USER_PROFILE_RESPONSE, (message) => {
      const res = message.userProfileResponse;
      if (res) setProfile(res);
    });
    return () => off();
  }, [onMessage, setProfile]);

  const gameStatus = useMemo(
    (): GameStatus => ({
      multiplier,
      gameState,
      countdown,
      countdownTotal,
      countdownKey: deadlineVersion,
      history,
      missedWindows,
    }),
    [
      multiplier,
      gameState,

      countdown,
      countdownTotal,
      deadlineVersion,
      history,
      missedWindows,
    ]
  );

  return gameStatus;
};
